import { API_BASE } from "./config";

// Admin auth token storage (separate from candidate/recruiter tokens)
const ADMIN_TOKEN_KEY = "admin_access_token";

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (token: string) => localStorage.setItem(ADMIN_TOKEN_KEY, token);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

/** A row in the admin jobs table. Mirrors AdminJobsController.listJobs select. */
export interface AdminJobRow {
  id: string;
  title: string;
  status: "DRAFT" | "OPEN" | "HOLD" | "IN_PROGRESS" | "CLOSED" | "ARCHIVED";
  companyName: string;
  companyId: string | null;
  orgId: string | null;
  isPublic: boolean;
  slug: string | null;
  publishedAt: string | null;
  createdAt: string;
  closedAt: string | null;
  closeReason: string | null;
  archivedAt: string | null;
  recruiter: { id: string; name: string; email: string } | null;
  organization: { id: string; name: string } | null;
  _count: { candidates: number; interviews: number };
}

/** A menu item of a product, from AdminProductModulesService.list. */
export interface ProductModuleRow {
  key: string;
  name: string;
  groupName: string | null;
  description: string | null;
  displayOrder: number;
  enabled: boolean;
  updatedAt: string;
}

export interface ProductModulesState {
  product: "THERESUME" | "THERECRUIT";
  modules: ProductModuleRow[];
  /** False for admins who may only look (DEVELOPER, READONLY). */
  canEdit: boolean;
  lastChange: { at: string; by: string } | null;
}

export type FeatureFlagAudience = "OFF" | "TESTERS" | "EVERYONE";

/** A release switch, from AdminFeatureFlagsService.list. */
export interface FeatureFlagRow {
  product: "THERESUME" | "THERECRUIT";
  key: string;
  name: string;
  description: string | null;
  audience: FeatureFlagAudience;
  testerEmails: string[];
  updatedAt: string;
  lastChange: { at: string; by: string } | null;
}

export interface FeatureFlagsState {
  flags: FeatureFlagRow[];
  /** False for admins who may only look (DEVELOPER, READONLY). */
  canEdit: boolean;
}

async function request<T>(method: string, url: string, body?: any, init?: RequestInit): Promise<T> {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  const token = getAdminToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${url}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

export interface MissingPdfSample {
  resumeId: string;
  userId: string;
  title?: string;
}

/** Progress of a read-only scan for candidates with no rendered resume PDF in S3. */
export interface ResumePdfAuditProgress {
  jobId: string;
  status: "RUNNING" | "COMPLETED" | "FAILED";
  totalDocs: number;
  checkedDocs: number;
  withPdf: number;
  missingPdf: number;
  skippedDocs: number;
  sampleMissing: MissingPdfSample[];
  error?: string;
  startedAt: string;
  lastUpdated: string;
  finishedAt?: string;
}

/** A First Round pass on theResume, as AdminFirstRoundService.describePass returns it. */
export interface FirstRoundPassRow {
  id: string;
  planId: string;
  planName: string;
  tier: string;
  state: "unstarted" | "active" | "ended";
  source: "stripe" | "coupon" | "admin" | null;
  couponCode: string | null;
  amountPaid: number;
  currency: string;
  accessDays: number | null;
  purchasedAt: string;
  startedAt: string | null;
  endsAt: string;
  extraInterviews: number;
  endedReason: string | null;
  reason: string | null;
}

/** A theResume user's First Round plan (AdminFirstRoundService.getUserPlan). */
export interface FirstRoundUserPlan {
  planChosenAt: string | null;
  demoTriesUsed: number;
  current: FirstRoundPassRow | null;
  practice: { used: number; limit: number | null; extra: number } | null;
  passes: FirstRoundPassRow[];
  redemptions: Array<{
    id: string;
    code: string;
    redeemedAt: string;
    finalAmount: number;
    passId: string | null;
  }>;
}

export interface CouponRedemptionsState {
  coupon: any;
  redemptions: Array<{
    id: string;
    redeemedAt: string;
    finalAmount: number;
    user: { id: string; email: string | null; name: string | null };
    pass: { id: string; planName: string; state: string; endsAt: string } | null;
    practisedSinceRedeeming: boolean;
  }>;
}

export const adminApi = {
  // Auth
  login: (dto: { email: string; password: string }) =>
    request<{ requiresTOTP: boolean; tempToken?: string; accessToken?: string }>(
      "POST",
      `/admin/auth/login`,
      dto
    ),
  verifyTOTP: async (dto: { tempToken: string; code: string }) => {
    // No header token for verify; tempToken provided in body
    return request<{ accessToken: string; user: any }>("POST", `/admin/auth/verify-totp`, dto);
  },
  setupTOTP: () =>
    request<{ secret: string; qrCode: string; backupCodes: string[] }>(
      "POST",
      `/admin/auth/setup-totp`
    ),

  // Features
  listFeatures: (product?: "THERESUME" | "THERECRUIT") =>
    request<any[]>("GET", `/admin/features${product ? `?product=${product}` : ""}`),
  getFeature: (id: string) => request<any>("GET", `/admin/features/${id}`),
  updateFeature: (id: string, dto: any) => request<any>("PATCH", `/admin/features/${id}`, dto),
  // The "theResume Menu" page: which menu items theResume shows.
  listProductModules: (product: "THERESUME" | "THERECRUIT" = "THERESUME") =>
    request<ProductModulesState>("GET", `/admin/product-modules?product=${product}`),
  updateProductModules: (
    modules: Record<string, boolean>,
    product: "THERESUME" | "THERECRUIT" = "THERESUME",
  ) => request<ProductModulesState>("PUT", `/admin/product-modules/${product}`, { modules }),
  listFeatureFlags: () => request<FeatureFlagsState>("GET", "/admin/feature-flags"),
  updateFeatureFlag: (
    flag: Pick<FeatureFlagRow, "product" | "key">,
    change: { audience: FeatureFlagAudience; testerEmails: string[] }
  ) =>
    request<FeatureFlagsState>(
      "PUT",
      `/admin/feature-flags/${flag.product}/${encodeURIComponent(flag.key)}`,
      change
    ),
  updateTierLimit: (id: string, tier: string, dto: { monthlyLimit: number | null }) =>
    request<any>("PATCH", `/admin/features/${id}/tiers/${tier}`, dto),
  getFeatureAnalytics: (id: string, startDate: string, endDate: string) =>
    request<any>(
      "GET",
      `/admin/features/${id}/analytics?startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`
    ),

  // Pricing
  listPlans: (product?: "THERESUME" | "THERECRUIT") =>
    request<any[]>("GET", `/admin/pricing/plans${product ? `?product=${product}` : ""}`),
  updatePlan: (id: string, dto: any) => request<any>("PATCH", `/admin/pricing/plans/${id}`, dto),
  listPacks: (product?: "THERESUME" | "THERECRUIT") =>
    request<any[]>("GET", `/admin/pricing/packs${product ? `?product=${product}` : ""}`),
  updatePack: (id: string, dto: any) => request<any>("PATCH", `/admin/pricing/packs/${id}`, dto),
  listCoupons: (filters?: Record<string, any>) => {
    const qs = filters
      ? "?" +
        Object.entries(filters)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    return request<any[]>("GET", `/admin/pricing/coupons${qs}`);
  },
  createCoupon: (dto: any) => request<any>("PATCH", `/admin/pricing/coupons`, dto),
  updateCoupon: (id: string, dto: any) =>
    request<any>("PATCH", `/admin/pricing/coupons/${id}`, dto),
  listCouponRedemptions: (id: string) =>
    request<CouponRedemptionsState>("GET", `/admin/first-round/coupons/${id}/redemptions`),

  // First Round passes (theResume users)
  getFirstRoundUser: (userId: string) =>
    request<FirstRoundUserPlan>("GET", `/admin/first-round/users/${userId}`),
  grantFirstRoundPass: (userId: string, dto: { planId: string; startNow: boolean; reason: string }) =>
    request<FirstRoundUserPlan>("POST", `/admin/first-round/users/${userId}/grant-pass`, dto),
  addFirstRoundInterviews: (userId: string, dto: { count: number; reason: string }) =>
    request<FirstRoundUserPlan>("POST", `/admin/first-round/users/${userId}/add-interviews`, dto),
  endFirstRoundPass: (userId: string, dto: { passId: string; reason: string }) =>
    request<FirstRoundUserPlan>("POST", `/admin/first-round/users/${userId}/end-pass`, dto),

  // Users
  searchUsers: (dto: { product?: "THERESUME" | "THERECRUIT"; query?: string }) => {
    const params = new URLSearchParams();
    if (dto.product) params.set("product", dto.product);
    if (dto.query) params.set("query", dto.query);
    return request<any[]>("GET", `/admin/users/search?${params.toString()}`);
  },
  getUserDetails: (userId: string, product: "THERESUME" | "THERECRUIT") =>
    request<any>("GET", `/admin/users/${userId}?product=${product}`),
  getUserUsage: (
    userId: string,
    product: "THERESUME" | "THERECRUIT",
    startDate: string,
    endDate: string
  ) =>
    request<any>(
      "GET",
      `/admin/users/${userId}/usage?product=${product}&startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`
    ),
  adjustUserLimit: (
    userId: string,
    dto: {
      featureKey: string;
      additionalUnits: number;
      reason: string;
      product?: "THERESUME" | "THERECRUIT";
    }
  ) => request<any>("POST", `/admin/users/${userId}/adjust-limit`, dto),
  getUserSubscriptions: (userId: string, product: "THERESUME" | "THERECRUIT") =>
    request<any[]>("GET", `/admin/users/${userId}/subscriptions?product=${product}`),
  getUserPaymentOverview: (userId: string) =>
    request<any>("GET", `/admin/users/${userId}/payment-overview`),
  getUserPaymentHistory: (userId: string) =>
    request<any[]>("GET", `/admin/users/${userId}/payment-history`),
  changeUserPlan: (
    userId: string,
    dto: { subscriptionId: string; newPlanId: string; reason: string }
  ) => request<any>("POST", `/admin/users/${userId}/change-plan`, dto),
  refundUserPayment: (
    userId: string,
    dto: { paymentIntentId: string; amount?: number; reason: string }
  ) => request<any>("POST", `/admin/users/${userId}/refund`, dto),
  getUserOverview: (userId: string) => request<any>("GET", `/admin/users/${userId}/overview`),
  getUserResource: (
    userId: string,
    resource:
      | "resumes"
      | "practice-interviews"
      | "recruiter-interviews"
      | "applications"
      | "recruiter-calls"
      | "usage"
      | "activity",
    filters?: {
      page?: number;
      limit?: number;
      status?: string;
      product?: "THERESUME" | "THERECRUIT";
      dateFrom?: string;
      dateTo?: string;
    }
  ) => {
    const params = new URLSearchParams();
    if (filters?.page != null) params.set("page", String(filters.page));
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    if (filters?.status) params.set("status", filters.status);
    if (filters?.product) params.set("product", filters.product);
    if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.set("dateTo", filters.dateTo);
    const qs = params.toString();
    return request<any>("GET", `/admin/users/${userId}/resources/${resource}${qs ? `?${qs}` : ""}`);
  },

  // Recruiters
  searchRecruiters: (query?: string) =>
    request<any[]>("GET", `/admin/recruiters/search${query ? `?query=${query}` : ""}`),
  getRecruiterDetails: (recruiterId: string) =>
    request<any>("GET", `/admin/recruiters/${recruiterId}`),
  getRecruiterSubscriptions: (recruiterId: string) =>
    request<any[]>("GET", `/admin/recruiters/${recruiterId}/subscriptions`),
  getRecruiterPaymentOverview: (recruiterId: string) =>
    request<any>("GET", `/admin/recruiters/${recruiterId}/payment-overview`),
  getRecruiterPaymentHistory: (recruiterId: string) =>
    request<any[]>("GET", `/admin/recruiters/${recruiterId}/payment-history`),
  changeRecruiterPlan: (
    recruiterId: string,
    dto: { subscriptionId: string; newPlanId: string; reason: string }
  ) => request<any>("POST", `/admin/recruiters/${recruiterId}/change-plan`, dto),
  refundRecruiterPayment: (
    recruiterId: string,
    dto: { paymentIntentId: string; amount?: number; reason: string }
  ) => request<any>("POST", `/admin/recruiters/${recruiterId}/refund`, dto),
  adjustRecruiterLimit: (
    recruiterId: string,
    dto: {
      featureKey: string;
      additionalUnits: number;
      reason: string;
      scope: "individual" | "organization";
      organizationId?: string;
      expiresInDays?: number;
    }
  ) => request<any>("POST", `/admin/recruiters/${recruiterId}/adjust-limit`, dto),
  getRecruiterOverview: (recruiterId: string) =>
    request<any>("GET", `/admin/recruiters/${recruiterId}/overview`),
  getRecruiterResource: (
    recruiterId: string,
    resource:
      | "jobs"
      | "companies"
      | "applications"
      | "interviews"
      | "resumes"
      | "usage"
      | "activity",
    filters?: { page?: number; limit?: number; status?: string; dateFrom?: string; dateTo?: string }
  ) => {
    const params = new URLSearchParams();
    if (filters?.page != null) params.set("page", String(filters.page));
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    if (filters?.status) params.set("status", filters.status);
    if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.set("dateTo", filters.dateTo);
    const qs = params.toString();
    return request<any>(
      "GET",
      `/admin/recruiters/${recruiterId}/resources/${resource}${qs ? `?${qs}` : ""}`
    );
  },

  // Billing
  listBillingSubscriptions: (filters?: {
    status?: string;
    tier?: string;
    product?: "THERESUME" | "THERECRUIT";
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.tier) params.set("tier", filters.tier);
    if (filters?.product) params.set("product", filters.product);
    if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.set("dateTo", filters.dateTo);
    if (filters?.page != null) params.set("page", String(filters.page));
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    const qs = params.toString();
    return request<any>("GET", `/admin/billing/subscriptions${qs ? `?${qs}` : ""}`);
  },
  getBillingSubscription: (id: string) => request<any>("GET", `/admin/billing/subscriptions/${id}`),
  changeBillingPlan: (
    subscriptionId: string,
    dto: { newPlanId: string; reason: string; expectedRecruiterId?: string }
  ) => request<any>("POST", `/admin/billing/subscriptions/${subscriptionId}/change-plan`, dto),
  issueBillingRefund: (dto: { paymentIntentId: string; amount?: number; reason: string }) =>
    request<any>("POST", `/admin/billing/refund`, dto),
  getBillingRevenue: (product?: "THERESUME" | "THERECRUIT") =>
    request<any>("GET", `/admin/billing/revenue${product ? `?product=${product}` : ""}`),
  grantTopup: (dto: {
    recruiterId: string;
    featureKey: string;
    quantity: number;
    reason: string;
    expiresInDays?: number;
  }) => request<any>("POST", `/admin/billing/topup`, dto),

  // Analytics
  getOverview: (product?: "THERESUME" | "THERECRUIT") =>
    request<any>("GET", `/admin/analytics/overview${product ? `?product=${product}` : ""}`),
  getFeatureUsage: (product: "THERESUME" | "THERECRUIT", startDate: string, endDate: string) =>
    request<any[]>(
      "GET",
      `/admin/analytics/feature-usage?product=${product}&startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`
    ),
  getRevenue: (
    product?: "THERESUME" | "THERECRUIT",
    period: "daily" | "weekly" | "monthly" = "monthly"
  ) =>
    request<any>(
      "GET",
      `/admin/analytics/revenue?${product ? `product=${product}&` : ""}period=${period}`
    ),
  getChurn: (product?: "THERESUME" | "THERECRUIT") =>
    request<any>("GET", `/admin/analytics/churn${product ? `?product=${product}` : ""}`),
  getPlanDistribution: (filters?: {
    status?: string;
    tier?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.tier) params.set("tier", filters.tier);
    if (filters?.page != null) params.set("page", String(filters.page));
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    const qs = params.toString();
    return request<any[]>("GET", `/admin/analytics/plan-distribution${qs ? `?${qs}` : ""}`);
  },

  // Companies
  listCompanies: (status?: "unverified" | "flagged" | "all") =>
    request<any[]>(
      "GET",
      `/admin/companies${status && status !== "all" ? `?status=${status}` : ""}`
    ),
  verifyCompany: (id: string) => request<any>("POST", `/admin/companies/${id}/verify`),
  rejectCompany: (id: string, reason?: string) =>
    request<any>("POST", `/admin/companies/${id}/reject`, { reason }),
  getCompanyDeletionBlockers: (id: string) =>
    request<{
      company: { id: string; name: string; isActive: boolean };
      blockingJobs: Array<{ id: string; title: string; status: string }>;
      retainedJobCount: number;
    }>("GET", `/admin/companies/${id}/deletion-blockers`),
  /** Permanent delete. Server refuses unless the company has zero job postings. */
  purgeCompany: (id: string) =>
    request<{ success: boolean; id: string; name: string; assetsDeleted: number }>(
      "DELETE",
      `/admin/companies/${id}/purge`
    ),
  bulkPurgeCompanies: (companyIds: string[]) =>
    request<{
      requested: number;
      purged: number;
      failed: Array<{ id: string; error: string }>;
    }>("POST", `/admin/companies/bulk-purge`, { companyIds }),
  softDeleteCompany: (id: string, options: { closeAllJobs?: boolean; reason?: string } = {}) =>
    request<{ success: boolean; jobsClosed: number; applicationsClosed: number }>(
      "POST",
      `/admin/companies/${id}/soft-delete`,
      options
    ),

  // Jobs — bulk lifecycle controls for cleaning up stale postings.
  // Nothing here deletes candidate history; see purgeJob for the one exception.
  listJobs: (params: {
    status?: string;
    orgId?: string;
    companyId?: string;
    staleBefore?: string;
    search?: string;
    limit?: number;
  } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return request<{ jobs: AdminJobRow[]; total: number; returned: number }>(
      "GET",
      `/admin/jobs${suffix}`
    );
  },
  getJobImpact: (id: string) => request<any>("GET", `/admin/jobs/${id}/impact`),
  bulkJobImpact: (jobIds: string[]) =>
    request<{
      jobCount: number;
      applications: number;
      applicationsToClose: number;
      preserved: { interviews: number; interviewRecordings: number };
    }>("POST", `/admin/jobs/bulk-impact`, { jobIds }),
  bulkCloseJobs: (jobIds: string[], reason?: string) =>
    request<{
      requested: number;
      closed: number;
      applicationsClosed: number;
      failed: Array<{ id: string; error: string }>;
    }>("POST", `/admin/jobs/bulk-close`, { jobIds, reason }),
  bulkArchiveJobs: (jobIds: string[]) =>
    request<{
      requested: number;
      archived: number;
      failed: Array<{ id: string; error: string }>;
    }>("POST", `/admin/jobs/bulk-archive`, { jobIds }),
  reopenJob: (id: string, reopenApplications = false) =>
    request<any>("POST", `/admin/jobs/${id}/reopen`, { reopenApplications }),
  purgeJob: (id: string) => request<any>("DELETE", `/admin/jobs/${id}/purge`),

  // Organizations
  listOrganizations: () => request<any[]>("GET", `/admin/organizations`),

  // Location backfill
  startLocationBackfill: (dto: {
    target: "central" | "resumes_v2" | "applied";
    organizationId?: string;
    confirm?: boolean;
  }) => request<any>("POST", `/admin/location-backfill`, dto),
  getLocationBackfillCoverage: () =>
    request<{ coverage: any[] }>("GET", `/admin/location-backfill/coverage`),
  getActiveLocationBackfill: () => request<{ active: any }>("GET", `/admin/location-backfill/active`),
  getRecentLocationBackfills: () => request<{ jobs: any[] }>("GET", `/admin/location-backfill/recent`),
  getLocationBackfillStatus: (jobId: string) =>
    request<any>("GET", `/admin/location-backfill/${jobId}/status`),

  // Experience dates backfill (structured month/year dates on stored resumes)
  startExperienceDatesBackfill: (dto: {
    target: "central" | "byor" | "applied" | "resumes_v2";
    mode: "dry_run" | "write";
    organizationId?: string;
    limit?: number;
    confirm?: boolean;
  }) => request<any>("POST", `/admin/experience-dates-backfill`, dto),
  getExperienceDatesBackfillCoverage: () =>
    request<{ coverage: any[] }>("GET", `/admin/experience-dates-backfill/coverage`),
  getActiveExperienceDatesBackfill: () =>
    request<{ active: any }>("GET", `/admin/experience-dates-backfill/active`),
  getRecentExperienceDatesBackfills: () =>
    request<{ jobs: any[] }>("GET", `/admin/experience-dates-backfill/recent`),

  // Resume PDF audit — read-only scan for candidates with no rendered PDF
  startResumePdfAudit: () => request<ResumePdfAuditProgress>("POST", `/admin/resume-pdf-audit`),
  getActiveResumePdfAudit: () =>
    request<{ active: ResumePdfAuditProgress | null }>("GET", `/admin/resume-pdf-audit/active`),
  getRecentResumePdfAudits: () =>
    request<{ jobs: ResumePdfAuditProgress[] }>("GET", `/admin/resume-pdf-audit/recent`),
  getResumePdfAuditStatus: (jobId: string) =>
    request<ResumePdfAuditProgress>("GET", `/admin/resume-pdf-audit/${jobId}/status`),

  // Audit logs
  listAuditLogs: (filters?: {
    limit?: number;
    offset?: number;
    adminUserId?: string;
    action?: string;
    resource?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.limit != null) params.set("limit", String(filters.limit));
    if (filters?.offset != null) params.set("offset", String(filters.offset));
    if (filters?.adminUserId) params.set("adminUserId", filters.adminUserId);
    if (filters?.action) params.set("action", filters.action);
    if (filters?.resource) params.set("resource", filters.resource);
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    return request<any[]>("GET", `/admin/audit-logs?${params.toString()}`);
  },

  // CRM leads (Apex 25 demo funnel; read-only mirror of what is synced to Zoho)
  listCrmLeads: (filters: {
    search?: string;
    brand?: string;
    status?: string;
    syncStatus?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.brand) params.set("brand", filters.brand);
    if (filters.status) params.set("status", filters.status);
    if (filters.syncStatus) params.set("syncStatus", filters.syncStatus);
    if (filters.limit != null) params.set("limit", String(filters.limit));
    if (filters.offset != null) params.set("offset", String(filters.offset));
    return request<AdminCrmLeadList>("GET", `/admin/crm-leads?${params.toString()}`);
  },

  // One-off import of existing recruiters as Apex 25 leads. dryRun only counts.
  backfillRecruiters: (body: { dryRun: boolean; consent: "inferred" | "none" }) =>
    request<RecruiterBackfillResult>("POST", "/admin/crm-leads/backfill-recruiters", body),

  // One-off import of theResume's existing users as First Round leads. dryRun only counts.
  backfillCandidates: (body: { dryRun: boolean; consent: "inferred" | "none" }) =>
    request<CandidateBackfillResult>("POST", "/admin/crm-leads/backfill-candidates", body),
};

export interface CandidateBackfillResult {
  dryRun: boolean;
  consent: "inferred" | "none";
  candidates: number;
  skippedUnverified: number;
  skippedTest: number;
  alreadyLeads: number;
  toImport: number;
  imported: number;
}

export interface RecruiterBackfillResult {
  dryRun: boolean;
  consent: "inferred" | "none";
  recruiterRows: number;
  distinctEmails: number;
  skippedNoEmail: number;
  skippedTest: number;
  alreadyLeads: number;
  toImport: number;
  imported: number;
}

/** One row of the CrmLead table, as the admin console lists it. */
export interface AdminCrmLead {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  brand: string;
  source: string;
  status: string;
  marketingConsent: boolean;
  marketingConsentAt: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  landingPage: string | null;
  demoRoleId: string | null;
  demoBookedAt: string | null;
  demoReplayUrl: string | null;
  resultsReplayUrl: string | null;
  /** All of this person's recordings: PostHog (Apex 25) or Mixpanel (First Round). */
  recordingsUrl: string | null;
  zohoLeadId: string | null;
  syncStatus: string;
  syncError: string | null;
  syncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCrmLeadList {
  items: AdminCrmLead[];
  total: number;
  counts: {
    status: Record<string, number>;
    syncStatus: Record<string, number>;
    brand: Record<string, number>;
  };
}

export type ProductKey = "THERESUME" | "THERECRUIT";
